import { CSSProperties, useMemo, useState } from "react";
import cloneDeep from "lodash/cloneDeep";

import { ServiceOfferingProcessingTypes } from "@enums/service-offering-processing-types";
import { FormattedRpServiceConfigurationServiceOffering } from "@idw-types/data/rp-service-configuration";
import { sortNumbers } from "@utils/number-util";
import styles from "./sortable-service-offerings-container.less";
import { PLACEHOLDER_BORDER_STYLE } from "@constants/dnd-constants";
import { SortableDndItem } from "@containers/common/sortable-dnd-container/sortable-dnd-item/sortable-dnd-item";
import { SortableDndContainer } from "@containers/common/sortable-dnd-container/sortable-dnd-container";
import { DragEndEvent } from "@dnd-kit/core";

type SortableServiceOfferingsContainerProps = {
    isDisabled: boolean;
    processingType: ServiceOfferingProcessingTypes;
    processingTypeTitle: string;
    serviceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>;
    setServiceOfferingsForProcessingType: (serviceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>, processingType: ServiceOfferingProcessingTypes) => void;
};

export const SortableServiceOfferingsContainer = (props: SortableServiceOfferingsContainerProps) => {
    const { isDisabled, processingType, processingTypeTitle, serviceOfferings, setServiceOfferingsForProcessingType } = props;
    const [dndActiveId, setDndActiveId] = useState(null);

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            const oldIndex = active.data.current.sortable.index;
            const newIndex = over.data.current.sortable.index;

            const currentServiceOfferings = cloneDeep(serviceOfferings);
            const serviceOfferingAtNewIndex = cloneDeep(currentServiceOfferings[newIndex]);
            const [movedServiceOffering] = currentServiceOfferings.splice(oldIndex, 1);
            const updatedServiceOfferings: Array<FormattedRpServiceConfigurationServiceOffering> = [];

            currentServiceOfferings.forEach(so => {
                if (so.order > movedServiceOffering.order && so.order <= serviceOfferingAtNewIndex.order) {
                    updatedServiceOfferings.push({ ...so, order: so.order - 1 });
                } else if (so.order < movedServiceOffering.order && so.order >= serviceOfferingAtNewIndex.order) {
                    updatedServiceOfferings.push({ ...so, order: so.order + 1 });
                }
            });

            movedServiceOffering.order = serviceOfferingAtNewIndex.order;
            updatedServiceOfferings.push(movedServiceOffering);
            setServiceOfferingsForProcessingType(updatedServiceOfferings, processingType);
        }
    };

    const sortedServiceOfferings = useMemo(() => {
        return serviceOfferings.sort((a, b) => sortNumbers(a.order, b.order));
    }, [serviceOfferings]);

    const getServiceOfferingID = (serviceOfferingID: string) => {
        return `${processingType}-service-offering-${serviceOfferingID}`;
    };

    const serviceOfferingValues = useMemo(() => {
        return sortedServiceOfferings.map(so => getServiceOfferingID(so.serviceOfferingID));
    }, [sortedServiceOfferings]);

    const dndItemStyle: CSSProperties = {
        userSelect: "none",
        padding: "0.5rem",
        display: "flex",
        alignItems: "center"
    };

    const serviceOfferingElements: Array<(shouldApplyPlaceholder: boolean, activeId?: string) => React.ReactNode> = useMemo(() => {
        return sortedServiceOfferings.map(so => {
            const id = getServiceOfferingID(so.serviceOfferingID);

            return (shouldApplyPlaceholder: boolean, activeId: string) =>
            (<SortableDndItem
                key={id}
                showDragAndDropIcon={true}
                isActiveId={shouldApplyPlaceholder && activeId === id}
                id={id}
                additionalStyle={dndItemStyle}
                activeIdStyle={{ ...dndItemStyle, ...PLACEHOLDER_BORDER_STYLE }}
            ><>{so.name}</>
            </SortableDndItem>);
        });
    }, [sortedServiceOfferings, dndActiveId]);

    return (
        <div className={styles.sortableServiceOfferingsContainer}>
            <span className={styles.processingTypeTitle}>{processingTypeTitle}</span>
            <SortableDndContainer
                isDisabled={isDisabled}
                useDragOverlay={true}
                internalDragAndDropElements={serviceOfferingElements}
                internalDragAndDropElementsValues={serviceOfferingValues}
                handleDragEnd={handleDragEnd}
                activeId={dndActiveId}
                setActiveId={setDndActiveId}
                styles={{ border: "1px solid #dee2e6" }}
            />
        </div>
    );
};